/**
 * ASCIN-native auth client (multi-tenant JWT against /api/v1/ascin-auth).
 *
 * Coexists with the Firebase auth used elsewhere in EF: stores the
 * ASCIN JWT under `ascin_token` so the axios interceptor in `api.ts`
 * can prefer it when present.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const BASE = `${API_BASE_URL}/api/v1/ascin-auth`;

export interface AscinUser {
  id: string;
  tenant_id: string;
  email: string;
  full_name?: string | null;
  role: string;
  created_at: string;
  last_login_at?: string | null;
}

export interface AscinTenant {
  id: string;
  name: string;
  plan: string;
  created_at: string;
}

export interface AscinTokenResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  user: AscinUser;
}

const TOKEN_KEY = 'ascin_token';
const USER_KEY = 'ascin_user';
const TENANT_KEY = 'ascin_tenant';

export const ascinAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: (): AscinUser | null => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  getTenant: (): AscinTenant | null => {
    const raw = localStorage.getItem(TENANT_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),

  async register(payload: {
    email: string;
    password: string;
    full_name?: string;
    tenant_name?: string;
  }): Promise<AscinTokenResponse> {
    const { data } = await axios.post<AscinTokenResponse>(`${BASE}/register`, payload);
    ascinAuth._store(data);
    await ascinAuth._fetchTenant();
    return data;
  },

  async login(email: string, password: string): Promise<AscinTokenResponse> {
    const { data } = await axios.post<AscinTokenResponse>(`${BASE}/login`, { email, password });
    ascinAuth._store(data);
    await ascinAuth._fetchTenant();
    return data;
  },

  async me(): Promise<{ principal: any; user: AscinUser; tenant: AscinTenant }> {
    const { data } = await axios.get(`${BASE}/me`, {
      headers: { Authorization: `Bearer ${ascinAuth.getToken()}` },
    });
    if (data?.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    if (data?.tenant) localStorage.setItem(TENANT_KEY, JSON.stringify(data.tenant));
    return data;
  },

  async logout() {
    try {
      await axios.post(`${BASE}/logout`, null, {
        headers: { Authorization: `Bearer ${ascinAuth.getToken()}` },
      });
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TENANT_KEY);
  },

  _store(data: AscinTokenResponse) {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  },

  async _fetchTenant() {
    try {
      const { data } = await axios.get<AscinTenant>(`${BASE}/tenant`, {
        headers: { Authorization: `Bearer ${ascinAuth.getToken()}` },
      });
      localStorage.setItem(TENANT_KEY, JSON.stringify(data));
    } catch {
      /* tenant load is optional */
    }
  },
};

export default ascinAuth;
